import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import MDButton from "components/MDButton";
import { getData } from "utilities/common";

const UnsavedChangesContext = createContext(null);

/** DevTools: filter console by `[UnsavedChanges]`. Enable in production builds with localStorage.setItem('debugUnsavedChanges','1') + refresh. */
function unsavedLog(...args) {
  const enabled =
    process.env.NODE_ENV === "development" ||
    (typeof localStorage !== "undefined" && localStorage.getItem("debugUnsavedChanges") === "1");
  if (!enabled) return;
  console.log("[UnsavedChanges]", ...args);
}

function getModalClassName() {
  try {
    const raw = getData("adminsMode");
    return raw && JSON.parse(raw) === "hyperdrive" ? "bg-black cus-modal" : "cus-modal";
  } catch {
    return "cus-modal";
  }
}

/**
 * Global unsaved-changes guard (BrowserRouter has no useBlocker).
 * Register from a screen with useUnsavedChangesRegistration — only blocks when isDirty && !blocked.
 */
export function UnsavedChangesProvider({ children }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [guard, setGuard] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const guardRef = useRef(null);
  guardRef.current = guard;

  useEffect(() => {
    if (!guard) {
      setModalOpen(false);
      unsavedLog("guard cleared → modal closed");
      return;
    }
    unsavedLog("guard set", {
      isDirty: guard.isDirty,
      blocked: guard.blocked,
      formPath: guard.formPath,
      dirtyEffectiveWillBe: Boolean(guard.isDirty && !guard.blocked),
    });
  }, [guard]);

  const dirtyEffective = Boolean(guard?.isDirty && !guard?.blocked);
  const dirtyRef = useRef(false);
  useLayoutEffect(() => {
    dirtyRef.current = dirtyEffective;
    unsavedLog("dirtyEffective / dirtyRef", dirtyEffective);
  }, [dirtyEffective]);

  const formPathRef = useRef("");
  useLayoutEffect(() => {
    formPathRef.current = guard?.formPath ?? "";
  }, [guard?.formPath]);

  const historyGuardPushedRef = useRef(false);

  useLayoutEffect(() => {
    if (!dirtyEffective) {
      if (historyGuardPushedRef.current) {
        unsavedLog("history guard reset (no longer dirtyEffective)");
      }
      historyGuardPushedRef.current = false;
      return;
    }
    if (historyGuardPushedRef.current) return;
    const url = window.location.pathname + window.location.search + window.location.hash;
    window.history.pushState({ __unsavedChangesGuard: 1 }, "", url);
    historyGuardPushedRef.current = true;
    unsavedLog("history duplicate pushState (guard entry)", { url, historyLength: window.history.length });
  }, [dirtyEffective]);

  useEffect(() => {
    if (!dirtyEffective) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirtyEffective]);

  useEffect(() => {
    if (!dirtyEffective) return;
    unsavedLog("popstate listener attached (capture phase), dirtyEffective=true");
    const onPopState = () => {
      const formUrl = formPathRef.current;
      const now = window.location.pathname + window.location.search + window.location.hash;
      unsavedLog("popstate fired", {
        dirtyRef: dirtyRef.current,
        formUrl: formUrl || "(empty)",
        locationNow: now,
        historyLength: window.history.length,
      });
      if (!dirtyRef.current) {
        unsavedLog("popstate → ignored (dirtyRef is false)");
        return;
      }
      if (!formUrl) {
        unsavedLog("popstate → ignored (formPath empty)");
        return;
      }
      if (now !== formUrl) {
        unsavedLog("popstate → URL mismatch, replaceState + navigate(replace)", { now, formUrl });
        window.history.replaceState({ __unsavedChangesGuard: 1 }, "", formUrl);
        navigate(formUrl, { replace: true });
      } else {
        unsavedLog("popstate → same URL as form, pushState duplicate", { formUrl });
        window.history.pushState({ __unsavedChangesGuard: 1 }, "", formUrl);
      }
      setModalOpen(true);
      unsavedLog("popstate → setModalOpen(true)");
    };
    window.addEventListener("popstate", onPopState, true);
    return () => {
      unsavedLog("popstate listener removed");
      window.removeEventListener("popstate", onPopState, true);
    };
  }, [dirtyEffective, navigate]);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const confirmLeave = useCallback(() => {
    const fn = guardRef.current?.onConfirmLeave;
    setModalOpen(false);
    if (typeof fn === "function") fn();
  }, []);

  const promptLeave = useCallback(() => {
    if (guardRef.current?.isDirty && !guardRef.current?.blocked) {
      setModalOpen(true);
    }
  }, []);

  const value = useMemo(
    () => ({
      setGuard,
      promptLeave,
    }),
    [promptLeave]
  );

  const dialogClassName = getModalClassName();
  const isHyperdriveDialog = dialogClassName.includes("bg-black");
  const paperSx = isHyperdriveDialog
    ? { borderRadius: "12px", overflow: "hidden" }
    : {
        borderRadius: "20px",
        overflow: "hidden",
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.grey[200]}`,
        boxShadow: "0px 8px 32px rgba(46, 46, 58, 0.1)",
      };
  const backdropSx = {
    backgroundColor: isHyperdriveDialog ? "rgba(0, 0, 0, 0.4)" : "rgba(46, 46, 58, 0.45)",
    backdropFilter: "blur(4px)",
  };
  const titleSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    py: 2.5,
    px: 3,
    pb: 1.5,
    fontWeight: 600,
    fontSize: "1.25rem",
    lineHeight: 1.3,
    color: isHyperdriveDialog ? theme.palette.common.white : theme.palette.dark?.main ?? theme.palette.text.main,
  };
  const closeIconSx = {
    color: isHyperdriveDialog ? theme.palette.common.white : theme.palette.grey[600],
  };
  const bodyTextSx = {
    color: isHyperdriveDialog ? "rgba(255, 255, 255, 0.88)" : theme.palette.grey[600],
    fontSize: "0.9375rem",
    lineHeight: 1.55,
  };

  const title = guard?.title ?? "Unsaved changes";
  const message =
    guard?.message ??
    "You have unsaved changes. Leave this page without saving? Your edits will be lost.";

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <Dialog
        className={dialogClassName}
        open={modalOpen}
        onClose={closeModal}
        maxWidth="xs"
        fullWidth
        aria-labelledby="global-unsaved-changes-title"
        BackdropProps={{ sx: backdropSx }}
        PaperProps={{ elevation: 0, sx: paperSx }}
      >
        <DialogTitle id="global-unsaved-changes-title" component="div" sx={titleSx}>
          {title}
          <IconButton size="small" edge="end" aria-label="Close" onClick={closeModal} sx={closeIconSx}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
          <DialogContentText component="div" sx={bodyTextSx}>
            {message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1.5, flexWrap: "wrap" }}>
          <MDButton type="button" size="small" color="secondary" onClick={closeModal}>
            Keep editing
          </MDButton>
          <MDButton type="button" size="small" color="error" onClick={confirmLeave}>
            Leave without saving
          </MDButton>
        </DialogActions>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
}

/**
 * Register unsaved state for the current route. History guard + modal run only when
 * isDirty is true and blocked is false (e.g. blocked while loading initial form baseline).
 */
export function useUnsavedChangesRegistration({
  isDirty,
  blocked = false,
  formPath,
  onConfirmLeave,
  title,
  message,
}) {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) {
    throw new Error("useUnsavedChangesRegistration must be used within UnsavedChangesProvider");
  }
  const { setGuard, promptLeave } = ctx;

  useLayoutEffect(() => {
    setGuard({
      isDirty,
      blocked,
      formPath,
      onConfirmLeave,
      title,
      message,
    });
    return () => setGuard(null);
  }, [isDirty, blocked, formPath, onConfirmLeave, title, message, setGuard]);

  return { promptLeave };
}
